import {useRouter} from 'next/router';
import {useEffect, useState} from 'react';
import {isObjEmpty} from 'utils/helpers';

import {FilterType} from '../components/filters/MetroMobileFilters';

export interface FilterState {
  [filterId: string]: {
    selectedOptions?: string[];
    priceRange?: [number, number];
    searchTerm?: string;
    showAll?: boolean;
  };
}

export interface QuickFilterState {
  [filterId: string]: boolean;
}

interface UseCategoryFiltersType {
  filterState: FilterState;
  quickFilterState: QuickFilterState;
  isInitialized: boolean;
  activeFiltersCount: number;
  updateFilterState: (
    filterId: string,
    updater: (prev: FilterState[string]) => FilterState[string],
    triggerChange?: boolean,
  ) => void;
  updateQuickFilterState: (filterId: string, value: boolean) => void;
  handleOptionToggle: (filterId: string, optionId: string) => void;
  handlePriceRangeChange: (filterId: string, value: [number, number]) => void;
  handleSearchChange: (filterId: string, searchTerm: string) => void;
  handleShowAllToggle: (filterId: string) => void;
  clearAllFilters: () => Promise<void>;
}

interface FilterConfig {
  id: string;
  type: FilterType;
  options?: Array<{id: string; value?: any}>;
}

export function useCategoryFilters(
  filters: FilterConfig[],
  quickFilters: FilterConfig[],
  onFilterChange?: (filterState: FilterState, quickFilterState: QuickFilterState) => void,
): UseCategoryFiltersType {
  const {push, query, pathname, isReady} = useRouter();

  const [filterState, setFilterState] = useState<FilterState>({});
  const [quickFilterState, setQuickFilterState] = useState<QuickFilterState>({});

  const [isInitialized, setIsInitialized] = useState(false);
  const [isUpdatingUrl, setIsUpdatingUrl] = useState(false);

  // Initialize state from URL params
  const getInitialState = (): FilterState => {
    const initialState: FilterState = {};

    // First, initialize all defined filters
    filters.forEach((filter) => {
      initialState[filter.id] = {
        selectedOptions: [],
        showAll: false,
        searchTerm: '',
      };

      if (filter.type === 'checkbox') {
        const queryValue = query[filter.id];
        if (queryValue) {
          if (Array.isArray(queryValue)) {
            initialState[filter.id].selectedOptions = queryValue.flatMap((v) => v.split(','));
          } else {
            initialState[filter.id].selectedOptions = queryValue.split(',');
          }
        }
      }

      // Special handling for quantity_value__in - convert values back to option IDs
      if (filter.id === 'quantity_value__in' && filter.options && initialState[filter.id].selectedOptions) {
        const selectedValues = initialState[filter.id].selectedOptions || [];

        initialState[filter.id].selectedOptions = selectedValues
          .map((value) => {
            const option = filter.options?.find((opt) => opt.value?.toString() === value);
            return option?.id || value;
          })
          .filter((val) => val !== undefined);
      }

      if (filter.type === 'price-range') {
        const min = query['price__gte'];
        const max = query['price__lte'];
        if (min && max) {
          initialState[filter.id].priceRange = [parseInt(min as string), parseInt(max as string)];
        }
      }
    });

    // Handle URL parameters that don't have corresponding filters yet (e.g., when filters are still loading)
    // This ensures brand_id__in and other params are preserved, but skips non-filter reserved params
    const knownFilterIds = filters.map((f) => f.id);
    const knownQuickFilterIds = quickFilters.map((f) => f.id);
    const priceRelatedParams = ['price__gte', 'price__lte'];
    const reservedNonFilterParams = ['slug', 'query', 'offset', 'limit', 'page'];

    Object.keys(query).forEach((paramKey) => {
      // Skip reserved non-filter parameters
      if (reservedNonFilterParams.includes(paramKey)) return;

      // Skip price-related params (handled above)
      if (priceRelatedParams.includes(paramKey)) return;

      // Skip already processed filter params and quick filter params
      if (knownFilterIds.includes(paramKey) || knownQuickFilterIds.includes(paramKey)) return;

      // Handle unknown checkbox-like parameters
      const queryValue = query[paramKey];
      if (queryValue && typeof queryValue === 'string') {
        initialState[paramKey] = {
          selectedOptions: queryValue.split(','),
          showAll: false,
          searchTerm: '',
        };
      }
    });

    return initialState;
  };

  const getInitialQuickState = (): QuickFilterState => {
    const initialQuickState: QuickFilterState = {};
    quickFilters.forEach((filter) => {
      const value = query[filter.id];
      if (filter.id === 'stock_quantity__gt' || filter.id === 'old_price__gt') {
        initialQuickState[filter.id] = value === '1';
      } else {
        initialQuickState[filter.id] = value === 'true';
      }
    });
    return initialQuickState;
  };

  // Helper function to build query string (similar to your existing helper)
  const buildQueryParams = (currentFilterState: FilterState, currentQuickFilterState: QuickFilterState) => {
    // Filter out undefined values from query
    const filteredQuery: Record<string, string | string[]> = Object.entries(query)
      .filter(([, value]) => value !== undefined)
      .reduce((acc, [key, value]) => {
        acc[key] = value as string | string[];
        return acc;
      }, {} as Record<string, string | string[]>);

    const queryParams: Record<string, string | string[]> = {...filteredQuery};

    // Remove all filter params first
    [...filters, ...quickFilters].forEach((filter) => {
      delete queryParams[filter.id];
    });

    // Remove price-related params as they are handled separately
    delete queryParams['price__gte'];
    delete queryParams['price__lte'];

    // Remove slug as it's part of the path, not query params
    delete queryParams.slug;

    // Add current filter selections
    Object.entries(currentFilterState).forEach(([filterId, state]) => {
      const {selectedOptions, priceRange} = state;

      if (selectedOptions && selectedOptions.length > 0) {
        // Special handling for quantity_value__in - use value from options
        if (filterId === 'quantity_value__in') {
          const filter = filters.find((f) => f.id === filterId);
          if (filter?.options) {
            queryParams[filterId] = selectedOptions
              .map((optionId) => {
                const option = filter.options?.find((opt) => opt.id === optionId);
                return option?.value;
              })
              .filter((val) => val !== undefined);
          } else {
            queryParams[filterId] = selectedOptions.join(',');
          }
        } else {
          queryParams[filterId] = selectedOptions.join(',');
        }
      }

      if (priceRange) {
        queryParams['price__gte'] = String(priceRange[0]);
        queryParams['price__lte'] = String(priceRange[1]);
      }
    });

    // Add quick filters
    Object.entries(currentQuickFilterState).forEach(([filterId, isSelected]) => {
      if (isSelected) {
        if (filterId === 'stock_quantity__gt' || filterId === 'old_price__gt') {
          queryParams[filterId] = '1';
        } else {
          queryParams[filterId] = 'true';
        }
      }
    });

    return queryParams;
  };

  useEffect(() => {
    if (isReady && !isInitialized) {
      // Initialize if we have filters OR if we have URL parameters that need processing
      const hasFilters = filters.length > 0 || quickFilters.length > 0;
      const hasUrlParams = Object.keys(query).some(
        (key) => key !== 'slug' && query[key] !== undefined && query[key] !== '',
      );

      if (hasFilters || hasUrlParams) {
        const initialState = getInitialState();
        const initialQuickState = getInitialQuickState();
        setFilterState(initialState);
        setQuickFilterState(initialQuickState);
        setIsInitialized(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, filters.length, quickFilters.length, query]);

  // Re-initialize when filters change from empty to filled
  useEffect(() => {
    if (isReady && isInitialized) {
      // Only re-initialize if we have actual filters now (not just URL params)
      const hasActualFilters = filters.length > 0 || quickFilters.length > 0;

      if (hasActualFilters) {
        const initialState = getInitialState();
        const initialQuickState = getInitialQuickState();
        setFilterState(initialState);
        setQuickFilterState(initialQuickState);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.length, quickFilters.length]);

  // Reset filters when category slug changes
  useEffect(() => {
    if (isReady && isInitialized) {
      const initialState = getInitialState();
      const initialQuickState = getInitialQuickState();
      setFilterState(initialState);
      setQuickFilterState(initialQuickState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.slug]);

  useEffect(() => {
    if (!isInitialized || isUpdatingUrl) return;

    const updateUrl = async () => {
      const queryParams = buildQueryParams(filterState, quickFilterState);

      const currentQueryString = Object.entries(query)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => {
          if (Array.isArray(value)) {
            return value.map((v) => `${key}=${encodeURIComponent(v)}`).join('&');
          }
          return `${key}=${encodeURIComponent(value as string)}`;
        })
        .join('&');

      const newQueryString = Object.entries(queryParams)
        .filter(([key, value]) => key !== 'slug' && value !== undefined && value !== null && value !== '')
        .map(([key, value]) => {
          if (Array.isArray(value)) {
            return value.map((v) => `${key}=${encodeURIComponent(v)}`).join('&');
          }
          return `${key}=${encodeURIComponent(value as string)}`;
        })
        .join('&');

      if (currentQueryString !== newQueryString) {
        setIsUpdatingUrl(true);

        try {
          // Build the correct path with slug
          const currentSlug = query.slug;
          const slugValue = Array.isArray(currentSlug) ? currentSlug[0] : currentSlug;
          const correctPath = slugValue ? pathname.replace('[slug]', slugValue) : pathname;

          if (!isObjEmpty(queryParams)) {
            await push(`${correctPath}?${newQueryString}`, undefined, {shallow: true});
          } else {
            await push(correctPath, undefined, {shallow: true});
          }
        } finally {
          // Reset the updating flag after router push completes
          setIsUpdatingUrl(false);
        }
      }
    };

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState, quickFilterState, isInitialized]);

  // Update filter state function
  const updateFilterState = (
    filterId: string,
    updater: (prev: FilterState[string]) => FilterState[string],
    triggerChange = true,
  ) => {
    setFilterState((prev) => {
      const updated = {...prev, [filterId]: updater(prev[filterId] || {})};
      if (triggerChange) {
        onFilterChange?.(updated, quickFilterState);
      }
      return updated;
    });
  };

  const updateQuickFilterState = (filterId: string, value: boolean) => {
    setQuickFilterState((prev) => {
      const updated = {...prev, [filterId]: value};
      onFilterChange?.(filterState, updated);
      return updated;
    });
  };

  const handleOptionToggle = (filterId: string, optionId: string) => {
    updateFilterState(filterId, (prev) => {
      const selected = prev.selectedOptions || [];
      const exists = selected.includes(optionId);
      const newSelected = exists ? selected.filter((id) => id !== optionId) : [...selected, optionId];
      return {
        ...prev,
        selectedOptions: newSelected,
      };
    });
  };

  const handlePriceRangeChange = (filterId: string, value: [number, number]) => {
    updateFilterState(filterId, (prev) => ({
      ...prev,
      priceRange: value,
    }));
  };

  const handleSearchChange = (filterId: string, searchTerm: string) => {
    updateFilterState(
      filterId,
      (prev) => ({
        ...prev,
        searchTerm,
      }),
      false,
    );
  };

  const handleShowAllToggle = (filterId: string) => {
    updateFilterState(
      filterId,
      (prev) => ({
        ...prev,
        showAll: !prev.showAll,
      }),
      false,
    );
  };

  const getActiveFiltersCount = () =>
    Object.values(filterState).reduce((sum, state) => {
      let count = state.selectedOptions?.length || 0;
      // Count price range as active filter if it's set
      if (state.priceRange) {
        count += 1;
      }
      return sum + count;
    }, 0) + Object.values(quickFilterState).reduce((sum, isSelected) => sum + (isSelected ? 1 : 0), 0);

  const clearAllFilters = async () => {
    // Prevent URL updates while clearing
    setIsUpdatingUrl(true);

    const clearedFilterState: FilterState = {};
    filters.forEach((filter) => {
      clearedFilterState[filter.id] = {
        selectedOptions: [],
        priceRange: undefined,
        showAll: false,
        searchTerm: '',
      };
    });

    const clearedQuickFilterState: QuickFilterState = {};
    quickFilters.forEach((filter) => {
      clearedQuickFilterState[filter.id] = false;
    });

    setFilterState(clearedFilterState);
    setQuickFilterState(clearedQuickFilterState);

    try {
      // Build the correct path with slug for clearing filters
      const currentSlug = query.slug;
      const slugValue = Array.isArray(currentSlug) ? currentSlug[0] : currentSlug;
      const correctPath = slugValue ? pathname.replace('[slug]', slugValue) : pathname;

      // Preserve non-filter parameters like ordering
      const preservedParams: Record<string, string | string[]> = {};
      Object.entries(query).forEach(([key, value]) => {
        // Skip slug and filter parameters
        if (key === 'slug' || value === undefined) return;
        const isFilterParam = [...filters, ...quickFilters].some((filter) => filter.id === key);
        // Also skip price-related parameters
        const isPriceParam = key === 'price__gte' || key === 'price__lte';
        if (isFilterParam || isPriceParam) return;

        preservedParams[key] = value;
      });

      // Build query string with preserved parameters
      const preservedQueryString = Object.entries(preservedParams)
        .filter(([key, value]) => key !== 'slug' && value !== undefined && value !== null && value !== '')
        .map(([key, value]) => {
          if (Array.isArray(value)) {
            return value.map((v) => `${key}=${encodeURIComponent(v)}`).join('&');
          }
          return `${key}=${encodeURIComponent(value as string)}`;
        })
        .join('&');

      if (preservedQueryString) {
        await push(`${correctPath}?${preservedQueryString}`, undefined, {shallow: true});
      } else {
        await push(correctPath, undefined, {shallow: true});
      }
    } finally {
      // Reset the updating flag after router push completes
      setIsUpdatingUrl(false);
    }
  };

  return {
    filterState,
    quickFilterState,
    isInitialized,
    updateFilterState,
    updateQuickFilterState,
    handleOptionToggle,
    handlePriceRangeChange,
    handleSearchChange,
    handleShowAllToggle,
    clearAllFilters,
    activeFiltersCount: getActiveFiltersCount(),
  };
}
